import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import * as React from "react";
import {
  Datagrid,
  List,
  TextField,
  TopToolbar,
  useGetOne,
  useListContext,
  useRefresh,
} from "react-admin";
import useNotify from "../unit/useNotify";

import { CloseOutlined } from "@mui/icons-material";
import PostPagination from "../unit/PostPagination";

import sendRequest from "../dataProvider/sendRequest";
import CustomButtonCancel from "../unit/CustomButtonCancel";
import CustomButtonSubmit from "../unit/CustomButtonSubmit";
import CustomDateField from "../unit/CustomDateField";
import CustomExportButton from "../unit/CustomExportButton";
import CustomLoading from "../unit/CustomLoading";

import { hmLightTheme } from "../themes/hmTheme";
import CampaignsListAside from "../campaigns/CampaignsListAside";
import ApplicationLinkField from "../applications/ApplicationLinkField";
import CustomerIdLinkIdField from "../customers/CustomerIdLinkIdField";
import CustomerIdLinkUserIdField from "../customers/CustomerIdLinkUserIdField";
import CampaignsApplicationsFilters from "./CampaignsApplicationsFilters";
import CustomFilterButton from "../unit/CustomFilterButton";
import HmLoading from "../unit/HmLoading";
import NoResults from "../unit/NoResults";
import NoPermission from "../unit/NoPermission";
import { downloadCSV } from "react-admin";
import formatDate from "../unit/formatDate";
const CampaignsListActions = ({ ...props }) => {
  return (
    <>
      <TopToolbar sx={{ width: "100%" }}>
        <CustomFilterButton />
        <CustomExportButton />
      </TopToolbar>
    </>
  );
};
const Empty = () => {
  const { total } = useListContext();

  if (total == 0) {
    return <Typography sx={{ p: 5 }}>No Datas ***</Typography>;
  }

  return (
    <Box textAlign="center" m={1}>
      <Typography variant="h4" paragraph>
        No products available{" "}
      </Typography>
      <Typography variant="body1">Create one or import from a file</Typography>
    </Box>
  );
};

const campaignApplicationsTypeExporter = (records: any) => {
  let data = [];
  let title = {};

  data = records.map((record: any) => {
    const { id } = record;
    return {
      id: record?.iApplicationId ? record.iApplicationId : "",
      iUserId: record?.iUserId ? record.iUserId : "",
      campaign_name: record?.campaign_name ? record.campaign_name : "",
      signUpTime: record?.signUpTime
        ? formatDate(record.signUpTime, "YYYY-MM-DD")
        : "",
      dtDocusignComplete: record?.dtDocusignComplete
        ? formatDate(record.dtDocusignComplete, "YYYY-MM-DD")
        : "",
      sProductName: record?.sProductName ? record.sProductName : "",
    };
  });
  title = {
    id: "App ID",
    iUserId: "User ID",
    campaign_name: "Page Name",
    signUpTime: "Applicant Sign Up",
    dtDocusignComplete: "Submitted On",
    sProductName: "Product",
  };
  return {
    title: title,
    data: data,
  };
};
const CustomExporter = (records: any) => {
  const filterData = campaignApplicationsTypeExporter(records);

  let csvTitle = filterData.title;
  let data = filterData.data;
  const csvData = [{ ...csvTitle }, ...data];
  const csvDataStr = csvData.map((row) => Object.values(row).join(","));
  downloadCSV(csvDataStr.join("\n"), "Landing Pages_Applications_Submitted");
};

const campaignsApplicationsList = () => {
  const refresh = useRefresh();
  const [open, setOpen] = React.useState(false);
  const { successNotify, errorNotify, infoNotify } = useNotify();
  const [record, setRecord] = React.useState<{ total: number } | null>(null);
  const permissions =
    localStorage.getItem("bbaccess")?.toString().split(",") || [];
  if (!permissions.includes("personal_campaigns")) {
    return <NoPermission />;
  }
  const { data, isLoading, error } = useGetOne("total/campaign", { id: 1 });
  React.useEffect(() => {
    setRecord(data);
  }, [data]);

  const total = record ? record.total : 0;

  const CustomRowStyle = (record: any) => {
    if (!record) return {};

    if (record?.sStatus != "Declined" && record?.sStatus != "Expired") {
      return {
        backgroundColor: "#fff",
        "&.MuiTableRow-hover:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.04)",
        },
      };
    } else {
      return {
        backgroundColor: "#f5f5f5",
        "& td.MuiTableCell-root": {
          color: "gray",
        },
        "& td.MuiTableCell-root span": {
          color: "gray",
        },
        "& td.MuiTableCell-root a": {
          color: "gray",
        },
        "& td.MuiTableCell-root p": {
          color: "gray",
        },
      };
    }
  };
  return (
    <>
      <List
        actions={<CampaignsListActions />}
        filters={CampaignsApplicationsFilters}
        aside={
          <CampaignsListAside
            totalData={total}
            selected="campaignsToApplications"
          />
        }
        sort={{ field: "created_at", order: "DESC" }}
        exporter={CustomExporter}
        sx={{
          width: "100%",
          borderRadius: "0px",
          "& .MuiCard-root": {
            borderRadius: "0px",
          },
          "& .RaList-actions form": {
            flex: "0 1 350%",
          },
          "& .RaList-actions form .filter-field .MuiInputBase-input": {
            width: "230px",
          },
          "& .RaList-actions .RaFilterForm-filterFormInput:first-child .MuiInputBase-root::before":
            {
              display: "none",
            },
          "& .RaList-actions .filter-field:first-child .MuiFormControl-root": {
            marginLeft: 0,
          },
        }}
        empty={false}
        emptyWhileLoading={false}
        loading={<HmLoading />}
        pagination={<PostPagination />}
        perPage={25}
      >
        <Datagrid
          optimized
          rowClick={false}
          size="small"
          id="table-campaigns"
          className="hm-list-datagrid"
          empty={false}
          sx={{
            ".MuiTableCell-root": {
              padding: "6px",
            },
            ".RaDatagrid-headerCell:first-child": {
              borderTopLeftRadius: "0px",
            },
            ".RaDatagrid-headerCell:last-child": {
              borderTopRightRadius: "0px",
            },
            overflowY: "auto",
            "& .column-groups": {
              md: { display: "none" },
              lg: { display: "table-cell" },
            },
            "& .Mui-active": {
              color: "gray",
            },
            "& .MuiTableSortLabel-root": {
              padding: "5px",
            },
            "& .RaDatagrid-headerCell": {
              color: "gray",
              fontSize: "1rem",
              backgroundColor: "#edf4fb",
              wordWrap: "break-word",
            },
            "& .MuiTableCell-paddingCheckbox": {
              display: "none",
            },
            "& .MuiTableCell-root.column-id": {
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdSmallWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdSmallWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdSmallWidth,
            },
            "& .MuiTableCell-root.column-sUrl": {
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              wordBreak: "break-word",
              overflowX: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "normal",
            },
            "& .MuiTableCell-root.column-sName": {
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              wordBreak: "break-word",
              overflowX: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "normal",
            },
            "& .MuiTableCell-root.column-created_at": {
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdDateWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdDateWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdDateWidth,
              wordBreak: "break-word",
              overflowX: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "normal",
            },
            "& .MuiTableCell-root.column-column-action": {
              minWidth: "250px",
              width: "250px",
              maxWidth: "250px",
            },
          }}
        >
          <ApplicationLinkField
            source="iApplicationId"
            label="App #"
            sortBy="iId"
            sortable={false}
          />
          <CustomerIdLinkUserIdField
            source="iUserId"
            label="User #"
            sortBy={"iUserId"}
            sortable={false}
          />
          <TextField
            source="campaign_name"
            label="Page Name"
            sortable={false}
          />
          {/* <TextField source="share_token" label="Page Token" sortable={false} /> */}
          <CustomDateField
            source="signUpTime"
            label="Applicant Sign Up"
            onlyShowDate={true}
            sortable={false}
            cellClassName="dateText"
          />
          <CustomDateField
            source="dtDocusignComplete"
            label="Submitted On"
            onlyShowDate={true}
            sortable={false}
            cellClassName="dateText"
          />
          <TextField source="sProductName" label="Product" sortable={false} />
        </Datagrid>
        <CustomLoading />
      </List>
    </>
  );
};

export default campaignsApplicationsList;
